import { useEffect, useState } from 'react';
import { fetchLeadSection, restoreArchivedLead } from '../api';
import type { Lead } from '../types';

// Archived = missed rings older than 48 h and unreachable leads (rule on the server). Nothing is
// deleted; any new call, message or form from the number brings the lead back on its own.
export default function ArchivedLeads({ onSelect, onRestored }: { onSelect: (id: string) => void; onRestored: () => void }) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const load = () => fetchLeadSection('archived', { limit: 50 })
    .then((d) => { setLeads(d.leads); setTotal(typeof d.total === 'number' ? d.total : d.leads.length); })
    .catch((e) => setErr(e instanceof Error ? e.message : 'Could not load archived leads'));
  useEffect(() => { void load(); }, []);

  async function restore(id: string) {
    setBusy(id);
    try { await restoreArchivedLead(id); setLeads((rows) => rows.filter((row) => row.id !== id)); onRestored(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Restore failed'); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-slate-500">
        {total === null ? 'Loading…' : `${total} archived: missed rings older than 48 h and leads unreachable after 3 tries. Restore brings one back to the queue.`}
      </p>
      {err && <p className="rounded-lg bg-red-50 p-2 text-xs text-red-700">{err}</p>}
      {leads.map((lead) => (
        <div key={lead.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
          <button type="button" onClick={() => onSelect(lead.id)} className="min-w-0 flex-1 text-left">
            <div className="truncate text-sm font-medium text-slate-900">{lead.name || 'Unknown'}</div>
            <div className="text-[11px] text-slate-500">{lead.source} · {new Date(lead.created_at).toLocaleDateString()}</div>
          </button>
          <button type="button" disabled={busy === lead.id} onClick={() => void restore(lead.id)}
            className="flex-none rounded-lg border border-emerald-300 px-2.5 py-1 text-xs font-semibold text-emerald-700 disabled:opacity-50">
            {busy === lead.id ? 'Restoring…' : 'Restore'}
          </button>
        </div>
      ))}
    </div>
  );
}
